import { useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Clock, Loader2, RotateCw, Send, Server, XCircle } from "lucide-react";
import RequestProgressStepper, { type RequestProgressStep, type RequestProgressStepState } from "@/components/RequestProgressStepper";
import { getNetworkNameForChain } from "@/chains";
import {
  SEPOLIA_CHAIN_ID,
  type PodPortalRequest,
  type PodPortalRequestStatus,
} from "@/contracts/pod";

interface PodRequestTrackerProps {
  requests: PodPortalRequest[];
  variant?: "default" | "compact";
  /** When true, render all-pending PoD steps (before a request is created). */
  skeleton?: boolean;
  /** Source chain for skeleton step labels. */
  chainId?: number;
  className?: string;
}

const statusCopy: Record<PodPortalRequestStatus, { label: string; tone: string }> = {
  "wallet-signing": { label: "Wallet signing", tone: "text-muted-foreground" },
  "source-submitted": { label: "Submitted on source chain", tone: "text-blue-700 dark:text-blue-300" },
  "source-mined": { label: "Source tx mined", tone: "text-blue-700 dark:text-blue-300" },
  "target-mined": { label: "Mined on COTI", tone: "text-yellow-700 dark:text-yellow-300" },
  "callback-generated": { label: "Callback generated", tone: "text-yellow-700 dark:text-yellow-300" },
  "pod-pending": { label: "PoD request pending", tone: "text-yellow-700 dark:text-yellow-300" },
  "callback-errored": { label: "Callback errored", tone: "text-red-700 dark:text-red-300" },
  succeeded: { label: "Complete", tone: "text-green-700 dark:text-green-300" },
  failed: { label: "Failed", tone: "text-red-700 dark:text-red-300" },
  "burn-debt": { label: "Burn cleanup pending", tone: "text-yellow-700 dark:text-yellow-300" },
};

const completedStatuses: PodPortalRequestStatus[] = ["succeeded"];
const callbackErrorStatuses: PodPortalRequestStatus[] = ["callback-errored", "burn-debt"];
const failedStatuses: PodPortalRequestStatus[] = ["failed"];

const stateForStep = (
  stepIndex: number,
  currentIndex: number,
  status: PodPortalRequestStatus,
): RequestProgressStepState => {
  if (completedStatuses.includes(status)) return "complete";
  if (callbackErrorStatuses.includes(status)) return stepIndex < 4 ? "complete" : "error";
  if (failedStatuses.includes(status)) return stepIndex < 2 ? "complete" : stepIndex === 2 ? "error" : "pending";
  if (stepIndex < currentIndex) return "complete";
  if (stepIndex === currentIndex) return "current";
  return "pending";
};

const currentStepIndexByStatus: Record<PodPortalRequestStatus, number> = {
  "wallet-signing": 0,
  "source-submitted": 0,
  "source-mined": 2,
  "pod-pending": 2,
  "target-mined": 3,
  "callback-generated": 4,
  succeeded: 4,
  failed: 2,
  "callback-errored": 4,
  "burn-debt": 4,
};

const buildPodStepLabels = (chainId: number) => {
  const sourceChainName = getNetworkNameForChain(chainId);
  return [
    {
      id: "source-sent",
      label: "Sent",
      tooltip: `Sent to ${sourceChainName}. Local transaction submitted.`,
      icon: <Send className="h-4 w-4" />,
    },
    {
      id: "source-mined",
      label: "Created",
      tooltip: `Mined on ${sourceChainName}. Local transaction mined and request created.`,
      icon: <CheckCircle2 className="h-4 w-4" />,
    },
    {
      id: "target-mined",
      label: "COTI",
      tooltip: "Mined on COTI.",
      icon: <Server className="h-4 w-4" />,
    },
    {
      id: "callback-generated",
      label: "Callback",
      tooltip: "Callback generated.",
      icon: <RotateCw className="h-4 w-4" />,
    },
    {
      id: "callback-completed",
      label: "Done",
      tooltip: "Callback completed.",
      icon: <CheckCircle2 className="h-4 w-4" />,
    },
  ];
};

export const getSkeletonPodSteps = (chainId: number): RequestProgressStep[] =>
  buildPodStepLabels(chainId).map(step => ({ ...step, state: "pending" as const }));

const getRequestSteps = (request: PodPortalRequest): RequestProgressStep[] => {
  const currentIndex = currentStepIndexByStatus[request.status] ?? 0;
  return buildPodStepLabels(request.chainId).map((step, index) => ({
    ...step,
    state: stateForStep(index, currentIndex, request.status),
  }));
};

const StatusIcon = ({ status, compact }: { status: PodPortalRequestStatus; compact?: boolean }) => {
  const size = compact ? "h-3.5 w-3.5" : "h-4 w-4";
  if (status === "succeeded") return <CheckCircle2 className={`${size} text-green-700 dark:text-green-300`} />;
  if (status === "failed" || status === "callback-errored") return <XCircle className={`${size} text-red-700 dark:text-red-300`} />;
  if (status === "burn-debt") return <AlertTriangle className={`${size} text-yellow-700 dark:text-yellow-300`} />;
  if (status === "pod-pending") return <Loader2 className={`${size} animate-spin text-yellow-700 dark:text-yellow-300`} />;
  return <Clock className={`${size} text-blue-700 dark:text-blue-300`} />;
};

const PodRequestTracker = ({
  requests,
  variant = "default",
  skeleton = false,
  chainId = SEPOLIA_CHAIN_ID,
  className = "",
}: PodRequestTrackerProps) => {
  const compact = variant === "compact";
  const [showHistory, setShowHistory] = useState(false);
  const sortedRequests = [...requests].sort((a, b) => b.createdAt - a.createdAt);
  const visibleRequests = compact || !showHistory ? sortedRequests.slice(0, 1) : sortedRequests;
  const hasHistory = !compact && sortedRequests.length > 1;

  if (!skeleton && visibleRequests.length === 0) return null;

  const sectionClass = compact
    ? `w-full rounded-xl border border-border bg-muted/30 p-2.5 text-foreground ${className}`
    : `mt-8 w-full max-w-3xl rounded-2xl border border-border glass-panel p-4 text-foreground shadow-xl ${className}`;

  const title = compact ? "PoD status" : "PoD Requests";

  return (
    <section className={sectionClass}>
      <div className={`flex items-center justify-between ${compact ? "mb-2" : "mb-3"}`}>
        <h3 className={`font-semibold tracking-wide ${compact ? "text-xs text-muted-foreground" : "text-sm"}`}>
          {title}
        </h3>
        {hasHistory && (
          <button
            type="button"
            onClick={() => setShowHistory(prev => !prev)}
            className="flex shrink-0 items-center gap-1 rounded-full border border-border px-3 py-1 text-xs text-primary transition hover:border-primary/50 hover:bg-primary/10"
          >
            {showHistory ? "Hide history" : `Show history (${sortedRequests.length})`}
            {showHistory ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
        )}
      </div>

      {skeleton && visibleRequests.length === 0 && (
        <div>
          <p className={`mb-2 ${compact ? "text-[11px]" : "text-xs"} text-muted-foreground`}>
            PoD tracking starts after transfer is submitted.
          </p>
          <RequestProgressStepper
            steps={getSkeletonPodSteps(chainId)}
            size={compact ? "compact" : "default"}
          />
        </div>
      )}

      <div className="space-y-2">
        {visibleRequests.map(request => {
          const copy = statusCopy[request.status];
          return (
            <div
              key={request.id}
              className={`rounded-xl border border-border bg-muted/50 ${compact ? "p-2" : "p-3"}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <StatusIcon status={request.status} compact={compact} />
                  <div className="min-w-0">
                    <div className={compact ? "text-xs" : "text-sm"}>
                      {request.kind === "deposit" ? "Portal in" : "Portal out"} {request.amount} {request.token}
                    </div>
                    <div className={`${compact ? "text-[10px]" : "text-xs"} ${copy.tone}`}>{copy.label}</div>
                  </div>
                </div>
              </div>
              <RequestProgressStepper
                steps={getRequestSteps(request)}
                className={compact ? "mt-2" : "mt-4"}
                size={compact ? "compact" : "default"}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default PodRequestTracker;
